import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import styled from "styled-components"

import {
  useLazyGetInAppTopNotificationsQuery,
  useMarkAllAsReadMutation,
  useMarkAsReadMutation,
  useMarkAsUnreadMutation,
} from "store/api/notificationApi"
import { useAppDispatch } from "store/hooks"
import { showAccountSettingsModal } from "store/modalSlise"
import { setAccountSettingsTab } from "store/tempDataSlise"
import { ApiTypes } from "types"

import { useAnchoredFloatingPanelPosition } from "hooks/useAnchoredFloatingPanelPosition"
import { AccountSettingsTabId } from "pages/account/AccountSettingsPage/config"

import { NotificationCenterModalFooter } from "./NotificationCenterModalFooter"
import { NotificationCenterModalHeader } from "./NotificationCenterModalHeader"
import { NotificationCenterModalList } from "./NotificationCenterModalList"

const ModalContainer = styled.div`
  position: fixed;
  width: 400px;
  max-height: calc(100vh - 80px);
  background: var(--surface-1);
  border-radius: 8px;
  box-shadow:
    0px 24px 60px -16px rgba(15, 23, 42, 0.22),
    0px 8px 24px -12px rgba(71, 85, 105, 0.18);
  display: flex;
  flex-direction: column;
  z-index: 4100;
  overflow: hidden;

  @media only screen and (max-width: 500px) {
    right: 10px;
  }
`

interface NotificationCenterModalProps {
  onClose: () => void
  triggerRef: RefObject<HTMLDivElement | null>
}

export const NotificationCenterModal = ({ onClose, triggerRef }: NotificationCenterModalProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const modalRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [notifications, setNotifications] = useState<ApiTypes.Model.Notification[]>([])
  const [hasMore, setHasMore] = useState(true)
  const modalPosition = useAnchoredFloatingPanelPosition({
    anchorRef: triggerRef,
    panelSize: { width: 400, height: 640 },
    gap: 8,
    viewportPadding: 10,
  })

  const [fetchNotifications, { data, isLoading, isFetching }] = useLazyGetInAppTopNotificationsQuery()
  const [markAsRead] = useMarkAsReadMutation()
  const [markAsUnread] = useMarkAsUnreadMutation()
  const [markAllAsRead, { isLoading: isMarkingAllAsRead }] = useMarkAllAsReadMutation()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as HTMLElement) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as HTMLElement)
      ) {
        onClose()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("keydown", handleEscape)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [onClose, triggerRef])

  useLayoutEffect(() => {
    fetchNotifications()
  }, [])

  useLayoutEffect(() => {
    if (!data?.data) return

    setNotifications(prev => (prev.length === 0 ? data.data : prev))
    setHasMore(data.data.length >= 10)
  }, [data])

  const loadMoreNotifications = useCallback(() => {
    if (!hasMore || isFetching) return

    const lastNotificationId = notifications[notifications.length - 1]?.id
    if (lastNotificationId) {
      fetchNotifications({ max_id: lastNotificationId })
        .unwrap()
        .then(newData => {
          if (newData.data.length > 0) {
            setNotifications(prev => {
              const existingIds = new Set(prev.map(n => n.id))
              const newNotifications = newData.data.filter(n => !existingIds.has(n.id))
              return [...prev, ...newNotifications]
            })
            setHasMore(newData.data.length === 10)
          } else {
            setHasMore(false)
          }
        })
    }
  }, [notifications, hasMore, isFetching, fetchNotifications])

  const handleScroll = useCallback(() => {
    if (!listRef.current || !hasMore || isFetching) return

    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight

    if (scrollPercentage > 0.8) {
      loadMoreNotifications()
    }
  }, [hasMore, isFetching, loadMoreNotifications])

  useEffect(() => {
    const listElement = listRef.current
    if (listElement) {
      listElement.addEventListener("scroll", handleScroll)
      return () => listElement.removeEventListener("scroll", handleScroll)
    }
  }, [handleScroll])

  const handleToggleRead = async (id: number, isRead: boolean) => {
    if (isRead) {
      await markAsUnread(id)
      setNotifications(prev =>
        prev.map(notification => (notification.id === id ? { ...notification, is_read: false } : notification)),
      )
    } else {
      try {
        await markAsRead(id)
        setNotifications(prev =>
          prev.map(notification => (notification.id === id ? { ...notification, is_read: true } : notification)),
        )
      } catch (error) {
        console.error("Failed to mark notification as read:", error)
      }
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead()
      setNotifications([])
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error)
    }
  }

  const handleOpenSettings = () => {
    dispatch(setAccountSettingsTab(AccountSettingsTabId.NOTIFICATION_SETTINGS))
    dispatch(showAccountSettingsModal(true))
    onClose()
  }

  const handleSeeHistory = () => {
    navigate("/notifications")
    onClose()
  }

  return (
    <ModalContainer ref={modalRef} style={modalPosition}>
      <NotificationCenterModalHeader onOpenSettings={handleOpenSettings} onClose={onClose} />
      <NotificationCenterModalList
        listRef={listRef}
        notifications={notifications}
        isLoading={isLoading}
        isLoadingMore={isFetching}
        hasMore={hasMore}
        onToggleRead={handleToggleRead}
        onCardClick={onClose}
      />
      <NotificationCenterModalFooter
        onMarkAllAsRead={handleMarkAllAsRead}
        onSeeHistory={handleSeeHistory}
        isMarkingAllAsRead={isMarkingAllAsRead}
        isLoading={isLoading}
      />
    </ModalContainer>
  )
}
